import React, { useEffect, useState, useCallback } from 'react';
import { investigationStatementService } from '../services/api';
import { formatDate } from '../services/dateUtils';
import '../styles/InvestigationStatementsPanel.css';

function fmtDateTime(ts) {
  if (!ts) return '—';
  try { return formatDate(ts); } catch { return String(ts); }
}

function fmtAmount(v) {
  if (v == null) return '—';
  const n = Number(v);
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 });
}

function shortHash(h) {
  if (!h) return '—';
  return h.length > 18 ? `${h.slice(0, 10)}…${h.slice(-8)}` : h;
}

const MATCH_STATUS_META = {
  matched:          { label: '✓ Matched in graph',    cls: 'ok' },
  added:            { label: '✚ Added to graph',       cls: 'ok' },
  no_onchain_match: { label: '⚠ No on-chain match',    cls: 'warn' },
  not_checked:      { label: '— Not checked (cap)',    cls: 'muted' },
};

export default function InvestigationStatementsPanel({ investigationId, onGraphUpdated }) {
  const [uploads, setUploads]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [uploading, setUploading]     = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const [file, setFile]   = useState(null);
  const [label, setLabel] = useState('');

  const [expandedId, setExpandedId]   = useState(null);
  const [expandedTxs, setExpandedTxs] = useState([]);
  const [loadingTxs, setLoadingTxs]   = useState(false);

  const loadUploads = useCallback(async () => {
    try {
      setLoading(true);
      const res = await investigationStatementService.list(investigationId);
      setUploads(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Failed to load investigation statements:', e.response?.data || e.message);
    } finally {
      setLoading(false);
    }
  }, [investigationId]);

  useEffect(() => { loadUploads(); }, [loadUploads]);

  const handleUpload = async () => {
    if (!file) { setUploadError('Choose a statement file to upload (CSV, XLSX, or PDF)'); return; }
    setUploadError(null);
    try {
      setUploading(true);
      const res = await investigationStatementService.upload(investigationId, file, label || null);
      setFile(null);
      setLabel('');
      const fileInput = document.getElementById('inv-statement-file-input');
      if (fileInput) fileInput.value = '';
      await loadUploads();
      // If any rows were newly added to the graph, tell the parent to reload it
      // so the Graph tab reflects the merge without needing a manual refresh.
      if (res.data?.added_count > 0 && onGraphUpdated) onGraphUpdated();
    } catch (e) {
      setUploadError(e.response?.data?.detail || e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const toggleExpand = async (uploadId) => {
    if (expandedId === uploadId) { setExpandedId(null); setExpandedTxs([]); return; }
    setExpandedId(uploadId);
    setLoadingTxs(true);
    try {
      const res = await investigationStatementService.getTransactions(investigationId, uploadId);
      setExpandedTxs(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setExpandedTxs([]);
    } finally {
      setLoadingTxs(false);
    }
  };

  const handleDelete = async (uploadId) => {
    if (!window.confirm('Delete this statement and all its parsed transactions? This does not remove anything already merged into the graph.')) return;
    try {
      await investigationStatementService.delete(investigationId, uploadId);
      if (expandedId === uploadId) { setExpandedId(null); setExpandedTxs([]); }
      await loadUploads();
    } catch (e) {
      alert('Failed to delete: ' + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="invstmt-panel">
      <div className="invstmt-header">
        <h2>📄 Exchange / Bank Statements</h2>
        <p className="invstmt-subtitle">
          Upload an exchange or bank statement for this investigation. Rows with a transaction
          hash are automatically checked against this investigation's graph — matched if already
          there, fetched and added if not, or flagged if no on-chain transaction can be found at
          all (a real signal worth investigating, e.g. a doctored statement). This is separate
          from the standalone Statements page — uploads here belong only to this investigation.
        </p>
      </div>

      {/* ── Upload form ── */}
      <div className="invstmt-form">
        <div className="invstmt-form-row">
          <div className="invstmt-form-group">
            <label>File <span className="opt-label">(.csv, .xlsx, .pdf)</span></label>
            <input id="inv-statement-file-input" type="file" accept=".csv,.xlsx,.xls,.pdf"
              onChange={e => setFile(e.target.files?.[0] || null)} />
          </div>
          <div className="invstmt-form-group">
            <label>Label <span className="opt-label">(optional)</span></label>
            <input type="text" placeholder="e.g., VALR export Q1"
              value={label} onChange={e => setLabel(e.target.value)} />
          </div>
          <button className="invstmt-btn-primary" onClick={handleUpload} disabled={uploading}>
            {uploading ? '⏳ Uploading & correlating…' : '⬆ Upload & Correlate'}
          </button>
        </div>
        {uploadError && <div className="invstmt-error">⚠ {uploadError}</div>}
        <p className="invstmt-hint">
          Header row required for CSV/XLSX. PDF parsing only works on text-based (selectable)
          PDFs. Columns are matched heuristically — any exchange/bank export with recognisable
          date/amount/currency/transaction-hash columns will work, not just one specific format.
        </p>
      </div>

      {/* ── Upload history ── */}
      <div className="invstmt-uploads">
        {loading ? (
          <p className="invstmt-loading">Loading…</p>
        ) : uploads.length === 0 ? (
          <div className="invstmt-empty">No statements uploaded to this investigation yet.</div>
        ) : (
          <div className="invstmt-uploads-list">
            {uploads.map(u => (
              <div key={u.id} className="invstmt-upload-row">
                <div className="invstmt-upload-main" onClick={() => toggleExpand(u.id)}>
                  <div className="invstmt-upload-title">
                    <strong>{u.original_filename}</strong>
                    <span className={`invstmt-status status-${u.status}`}>{u.status}</span>
                  </div>
                  <div className="invstmt-upload-meta">
                    {u.label && <span className="invstmt-label-tag">{u.label}</span>}
                    <span>{u.row_count ?? 0} rows</span>
                    {u.matched_count > 0 && <span className="invstmt-pill ok">{u.matched_count} matched</span>}
                    {u.added_count > 0 && <span className="invstmt-pill ok">{u.added_count} added to graph</span>}
                    {u.unmatched_count > 0 && <span className="invstmt-pill warn">{u.unmatched_count} no on-chain match</span>}
                    <span className="invstmt-upload-by">by {u.uploaded_by}</span>
                    <span>{fmtDateTime(u.uploaded_at)}</span>
                  </div>
                  {u.status === 'failed' && u.parse_error && (
                    <div className="invstmt-error">⚠ {u.parse_error}</div>
                  )}
                </div>
                <div className="invstmt-upload-actions">
                  <button className="invstmt-icon-btn danger" title="Delete"
                    onClick={() => handleDelete(u.id)}>🗑</button>
                </div>

                {expandedId === u.id && (
                  <div className="invstmt-tx-detail">
                    {loadingTxs ? (
                      <p className="invstmt-loading">Loading transactions…</p>
                    ) : expandedTxs.length === 0 ? (
                      <div className="invstmt-empty">No transactions parsed from this file.</div>
                    ) : (
                      <div className="invstmt-table-scroll">
                        <table className="invstmt-table">
                          <thead>
                            <tr>
                              <th>Date</th><th>Description</th><th>Amount</th><th>Dir</th>
                              <th>Currency</th><th>TX Hash</th><th>From</th><th>To</th><th>Match</th>
                            </tr>
                          </thead>
                          <tbody>
                            {expandedTxs.map(r => {
                              const meta = MATCH_STATUS_META[r.match_status] || null;
                              return (
                                <tr key={r.id}>
                                  <td>{fmtDateTime(r.tx_date)}</td>
                                  <td>{r.description || r.counterparty || '—'}</td>
                                  <td className={`amt-${r.direction}`}>{fmtAmount(r.amount)}</td>
                                  <td>{r.direction || '—'}</td>
                                  <td>{r.currency || '—'}</td>
                                  <td>
                                    {r.tx_hash
                                      ? <code className="invstmt-hash-code" title={r.tx_hash}>{shortHash(r.tx_hash)}</code>
                                      : '—'}
                                  </td>
                                  <td>
                                    {r.address_from
                                      ? <code className="invstmt-addr-code" title={r.address_from}>{shortHash(r.address_from)}</code>
                                      : '—'}
                                  </td>
                                  <td>
                                    {r.address_to
                                      ? <code className="invstmt-addr-code" title={r.address_to}>{shortHash(r.address_to)}</code>
                                      : '—'}
                                  </td>
                                  <td>
                                    {meta
                                      ? <span className={`invstmt-match-pill ${meta.cls}`}>{meta.label}</span>
                                      : <span className="invstmt-match-pill muted">— No TX hash</span>}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
